import { Command } from 'ckeditor5/src/core';
import type { Editor } from 'ckeditor5/src/core';

/**
 * Type definition for the SmartButtonCommand's value
 */
export interface SmartButtonCommandValue {
	text: string;
	url: string;
	target: string;
	alignment: string;
}

export default class SmartButtonCommand extends Command {
	/**
	 * Command name
	 */
	public static COMMAND_NAME = 'smartButton';

	/**
	 * @inheritDoc
	 */
	constructor( editor: Editor ) {
		super( editor );
	}

	/**
	 * @inheritDoc
	 */
	public override refresh(): void {
		const model = this.editor.model;
		const selection = model.document.selection;

		// Check if the current selection has a smartButton attribute
		if ( selection.hasAttribute( 'smartButton' ) ) {
			this.isEnabled = true;

			// Store current values in the command
			const selectedBlock = Array.from( selection.getSelectedBlocks() )[ 0 ];
			const textNode = selectedBlock?.getChild( 0 );
			const text = textNode && textNode.is( '$text' ) ? textNode.data : '';

			this.value = {
				text: text || '',
				url: selection.getAttribute( 'smartButtonHref' ) || '', // Use our custom attribute
				target: selection.getAttribute( 'smartButtonTarget' ) || '_blank', // Use our custom attribute
				alignment:
					selection.getAttribute( 'smartButtonAlignment' ) || 'none'
			} as SmartButtonCommandValue;
		} else {
			// Can be inserted anywhere text is allowed
			this.isEnabled = true;
			this.value = null;
		}
	}

	/**
	 * Executes the command to insert a smart button.
	 *
	 * @param options Command options
	 */
	public override execute(
		options: {
			text?: string;
			url?: string;
			target?: string;
			alignment?: string;
		} = {}
	): void {
		const {
			text = 'Button',
			url = '',
			target = '_blank',
			alignment = 'none'
		} = options;
		const model = this.editor.model;

		model.change( writer => {
			// Create a range on the current selection
			const selection = model.document.selection;

			// Remove any existing selection content
			if ( !selection.isCollapsed ) {
				model.deleteContent( selection );
			}

			const smartButtonText = writer.createText( text, {
				smartButton: true,
				smartButtonAlignment: String( alignment )
			} );

			// Insert the smart button
			model.insertContent( smartButtonText, selection );

			const positionAfter = writer.createPositionAfter( smartButtonText );

			const range = writer.createRange(
				writer.createPositionBefore( smartButtonText ),
				positionAfter
			);
			writer.setSelection( range );

			const finalPosition = positionAfter;

			model.enqueueChange( () => {
				if ( url ) {
					this.editor.execute( 'link', url );
				}

				if ( target === '_blank' ) {
					model.change( writer => {
						writer.setAttribute( 'linkTarget', true, range );
					} );
				}

				model.change( writer => {
					writer.setSelection( finalPosition );
				} );
			} );
		} );
	}
}
